import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download, ArrowRight } from 'lucide-react';
import publications, { publicationTypes } from '../data/publicationsData';
import SEO from '../components/SEO';

const Publications = () => {
  const [activeType, setActiveType] = useState('All');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const filtered = activeType === 'All'
    ? publications
    : publications.filter(p => p.type === activeType);

  const featured = filtered.filter(p => p.featured);
  const rest = filtered.filter(p => !p.featured);

  return (
    <div className="min-h-screen bg-white" data-testid="publications-page">
      <SEO title="Publications & Resources" description="Download free guides, whitepapers, and legal resources on trademark registration, copyright law, and IP protection from Federal Trademark Experts." path="/resources/publications" />
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-900 text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block bg-blue-500/20 border border-blue-400/30 rounded-full px-4 py-2 mb-6">
            <span className="text-sm font-semibold text-blue-200">Free Resources</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6">
            Publications &
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300"> Resources</span>
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Download our comprehensive guides, whitepapers, and eBooks to master trademark protection
          </p>
        </div>
      </section>

      {/* Type Filters */}
      <section className="bg-white border-b border-slate-200 sticky top-[72px] z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto gap-2 py-4 justify-center scrollbar-hide" data-testid="pub-type-filters">
            {publicationTypes.map((type) => (
              <button
                key={type}
                onClick={() => setActiveType(type)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-200 ${
                  activeType === type
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Publications */}
      {featured.length > 0 && (
        <section className="py-12 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">Featured Publications</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {featured.map((pub) => (
                <Link key={pub.id} to={`/resources/publications/${pub.id}`} className="group" data-testid={`pub-featured-${pub.id}`}>
                  <article className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full">
                    <div className="relative overflow-hidden aspect-[16/9]">
                      <img src={pub.image} alt={pub.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute top-4 left-4">
                        <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded-full">{pub.type}</span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors">{pub.title}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed mb-4">{pub.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1"><FileText className="h-4 w-4" />{pub.pages}</span>
                          <span>{pub.size}</span>
                        </div>
                        <span className="text-blue-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                          View <ArrowRight className="h-4 w-4" />
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Publications */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {rest.length > 0 && <h2 className="text-2xl font-bold text-slate-900 mb-8">All Publications</h2>}
          <div className="flex flex-wrap justify-center gap-8" data-testid="publications-grid">
            {(featured.length === 0 ? filtered : rest).map((pub) => (
              <Link key={pub.id} to={`/resources/publications/${pub.id}`} className="group w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.33%-1.5rem)]" data-testid={`pub-card-${pub.id}`}>
                <article className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                  <div className="relative overflow-hidden aspect-[16/10]">
                    <img src={pub.image} alt={pub.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-4 left-4">
                      <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded-full">{pub.type}</span>
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="text-lg font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors leading-tight">{pub.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4 flex-1">{pub.description}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <div className="flex items-center gap-3 text-sm text-slate-500">
                        <span>{pub.pages}</span>
                        <span>PDF</span>
                        <span>{pub.size}</span>
                      </div>
                      <span className="text-blue-600 text-sm font-semibold flex items-center gap-1">
                        <Download className="h-4 w-4" /> Free
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">No publications found in this category.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Publications;
